import { useMemo, useState } from 'react';

import { useModals } from '@mantine/modals';

import { IconCheck, IconUser } from '@tabler/icons-react';

import { Avatar, ComboboxItem, Group, Loader, Select, SelectProps, Text } from '@optiro/ui';

import { AccountAvatar } from '@/components/AccountAvatar';
import { useAccounts } from '@/hooks/useAccounts';
import { Partner, usePartners } from '@/hooks/usePartners/usePartners';
import useMoveInterval from '@/modules/Planning/hooks/resources/useMoveInterval';
import { Account, Interval, Resource } from '@/types/types';

const MoveInterval = ({ resource, interval }: { resource: Resource; interval: Interval }) => {
  const { data: accounts } = useAccounts();
  const { data: partners } = usePartners();
  const moveInterval = useMoveInterval({ resource });
  const modal = useModals();

  const [selectedId, setSelectedId] = useState<string | null>(resource.resourceable_id.toString());

  const handleChange = (value: string | null) => {
    if (!value) return;
    setSelectedId(value);
    if (selectedId) {
      moveInterval.mutate(
        {
          intervalId: interval.id,
          newResourceableId: Number(value),
        },
        {
          onSuccess: () => {
            modal.closeModal('edit-interval');
            setSelectedId(null);
          },
        }
      );
    }
  };

  const selectedAccount = useMemo(() => {
    if (!selectedId || !accounts) return null;
    return accounts.find((account) => String(account.id) === selectedId) ?? null;
  }, [accounts, selectedId]);

  if (!accounts) return <Loader size="sm" />;
  if (!partners) return <Loader size="sm" />;

  const accountData: ComboboxItem[] = accounts.map((account) => ({
    value: String(account.id),
    label: account.name || account.email || '',
  }));

  const partnerData: ComboboxItem[] = partners.map((partner) => ({
    value: String(partner.id),
    label: partner.name,
  }));

  const renderOption: SelectProps['renderOption'] = ({ checked, option }) => {
    if (resource.resourceable_type === 'Account') {
      const account = accounts.find((acc) => String(acc.id) === option.value);
      if (!account) return null;
      return renderOptionAccount({ checked, account });
    }

    const partner = partners.find((part) => String(part.id) === option.value);

    if (!partner) return null;

    return renderOptionPartner({ checked, partner });
  };

  const renderOptionAccount = ({
    checked,
    account,
  }: {
    checked: boolean | undefined;
    account: Account;
  }) => {
    return (
      <Group key={account.id} wrap="nowrap">
        {checked && <IconCheck size={18} />}

        <AccountAvatar photoUrl={account.photo.thumb.url} name={account.name} radius="xl" />
        <div>
          <Text size="sm">{account.name}</Text>
          <Text size="xs" c="dimmed">
            {account.company_account_category?.name}
          </Text>
        </div>
      </Group>
    );
  };

  const renderOptionPartner = ({
    checked,
    partner,
  }: {
    checked: boolean | undefined;
    partner: Partner;
  }) => {
    return (
      <Group key={partner.id} wrap="nowrap">
        {checked && <IconCheck size={18} />}

        <Avatar color="orange" alt={partner.name} radius="xl">
          <IconUser size={14} />
        </Avatar>
        <div>
          <Text size="sm">{partner.name}</Text>
        </div>
      </Group>
    );
  };

  return (
    <Select
      label="Ressource"
      placeholder="Vælg en ressource"
      data={resource.resourceable_type === 'Account' ? accountData : partnerData}
      value={selectedId}
      size="md"
      leftSection={
        selectedAccount ? (
          <AccountAvatar
            photoUrl={selectedAccount.photo?.thumb?.url}
            name={selectedAccount.name}
            radius="xl"
            size={20}
          />
        ) : (
          <IconUser size={16} />
        )
      }
      onChange={handleChange}
      renderOption={({ ...props }) => renderOption(props)}
      error={
        moveInterval.isError
          ? 'Den valgte ressource har allerede en booking i tidsrammen'
          : undefined
      }
      searchable
      nothingFoundMessage="Ingen ressourcer fundet"
    />
  );
};

export { MoveInterval };
